import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useContext, useEffect, useRef, useState } from 'react';
import { buildFormData, scrollToError } from '../../../utils/HelperFunctions';
import { generalCreate, generalGet } from '../../../API/api';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authContext } from '../../../store/context/authContext';
import { IPageTableItem } from '../types/interfaces';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';

const CreatePageForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<IPageTableItem | null>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { catchError } = useContext(authContext);

  const { data, isSuccess, isLoading } = useQuery({
    queryKey: ['body type data', id],
    queryFn: () => generalGet(`/fuel-types/${id}`),
    // enabled: !!id
    enabled: false
  });

  useEffect(() => {
    const reqData = data?.data.data.page;
    if (isSuccess) {
      setPageData(reqData);
    }
  }, [isSuccess, data, id]);

  useEffect(() => {
    if (!id) {
      setPageData(null);
    }
  }, [id]);

  const handleSubmit = (values: any) => {
    let route = '/body-type/store';
    if (id) route = `/body-type/${id}/update`;
    setLoading(true);
    const formData = new FormData();
    buildFormData(formData, values, '');
    generalCreate({ route, values: formData })
      .then((res) => {
        toast.success(`${t('page')} ${!id ? t('created') : t('updated')} ${t('successfully')}`);
        navigate('/fuel-types');
      })
      .catch((error) => {
        catchError(error, setLoading);
      });
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t('required'))
      .matches(/^[\u0600-\u06FF\s]+$/, t('must_ar'))
      .min(3, t('name_short'))
  });

  const initialValues: IPageTableItem = {
    ...pageData,
    
    name: pageData?.name || ''
  };

  if (isLoading) return <FormDynamicSkeleton sections={[[{ columns: 2, items: 1 }]]} buttons={1} />;

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value });
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('page_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper
                    title={t('name')}
                    inputName={`name`}
                    inputPlaceholder={t('name')}
                    inputError={formik.errors.name as string}
                    inputTouched={formik.touched.name as boolean}
                    input
                    noPadding
                  />
                  <FieldWrapper noPadding />
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={loading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('submit')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreatePageForm;
